import {
  getKeywordsForCategory,
  normalizeCategoryLabel,
} from './industryKeywordMap';

const MAX_CANDIDATES = 24;
const MAX_VALIDATIONS_DEFAULT = 12;
const MIN_VIABLE_COMPETITORS = 15;
const EARTH_RADIUS_MILES = 3958.8;

const LOCATION_STOP_WORDS = new Set([
  'usa',
  'us',
  'st',
  'street',
  'road',
  'rd',
  'ave',
  'avenue',
  'dr',
  'drive',
  'suite',
  'ste',
  'unit',
]);

const DIRECTORY_TYPES = new Set([
  'corporate_office',
  'point_of_interest',
  'establishment',
  'finance',
  'local_government_office',
]);

/**
 * Parse city and state from a Google formatted address.
 */
export function parseCityFromAddress(address) {
  if (!address) {
    return { city: '', state: '', marketLabel: '' };
  }

  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) {
    return { city: parts[0] || '', state: '', marketLabel: parts[0] || '' };
  }

  const city = parts[parts.length - 3] || parts[parts.length - 2] || '';
  const stateZip = parts[parts.length - 2] || '';
  const stateMatch = stateZip.match(/\b([A-Z]{2})\b/);
  const state = stateMatch ? stateMatch[1] : '';
  const marketLabel = state ? `${city}, ${state}` : city;

  return { city, state, marketLabel };
}

export function formatPrimaryType(primaryType) {
  if (!primaryType) return '';
  return primaryType.replace(/_/g, ' ').trim();
}

export function resolveCategoryLabel(anchor) {
  const directCandidates = [
    anchor?.primary_type_display,
    anchor?.primary_category,
    formatPrimaryType(anchor?.primary_type),
    anchor?.niche,
  ].filter(Boolean);

  for (const label of directCandidates) {
    const trimmed = label.trim();
    if (trimmed && getKeywordsForCategory(trimmed).primary) {
      return trimmed;
    }
  }

  for (const placeType of anchor?.types || []) {
    const formatted = formatPrimaryType(placeType);
    if (formatted && getKeywordsForCategory(formatted).primary) {
      return formatted;
    }
  }

  const name = (anchor?.name || '').toLowerCase();
  const nameCategoryHints = [
    [['junk', 'hauling', 'debris', 'dumpster'], 'junk removal service'],
    [['plumb', 'drain', 'sewer'], 'plumber'],
    [['hvac', 'heating', 'cooling', 'furnace'], 'HVAC contractor'],
    [['roof'], 'roofer'],
    [['electric'], 'electrician'],
    [['landscap', 'lawn', 'yard'], 'landscaper'],
    [['pest', 'exterminator'], 'pest control'],
    [['dentist', 'dental'], 'dentist'],
    [['clean', 'maid'], 'cleaning service'],
    [['garage door'], 'garage door repair'],
    [['tree', 'arborist'], 'tree service'],
    [['mov'], 'moving company'],
    [['dog walk', 'pet sit'], 'dog walker'],
    [['auto repair', 'mechanic'], 'auto repair'],
    [['paint'], 'house painter'],
    [['locksmith'], 'locksmith'],
    [['pool'], 'pool service'],
  ];

  for (const [keywords, category] of nameCategoryHints) {
    if (keywords.some((keyword) => name.includes(keyword))) {
      return category;
    }
  }

  return directCandidates[0]?.trim() || '';
}

function tokenizeLabel(value) {
  const tokens = (value || '').toLowerCase().match(/[a-z0-9]+/g) || [];
  return tokens.filter((token) => token.length > 2 && !LOCATION_STOP_WORDS.has(token));
}

function distinctiveNameTokens(anchor) {
  const nameTokens = tokenizeLabel(anchor?.name || '');
  if (!nameTokens.length) return [];

  const parsed = parseCityFromAddress(anchor?.address || '');
  const categoryLabel = resolveCategoryLabel(anchor);
  const mapped = getKeywordsForCategory(categoryLabel);

  const genericTokens = new Set(LOCATION_STOP_WORDS);
  tokenizeLabel(parsed.city).forEach((token) => genericTokens.add(token));
  tokenizeLabel(parsed.state).forEach((token) => genericTokens.add(token));
  tokenizeLabel(categoryLabel).forEach((token) => genericTokens.add(token));
  tokenizeLabel(mapped.primary).forEach((token) => genericTokens.add(token));
  mapped.alternates.forEach((alternate) => {
    tokenizeLabel(alternate).forEach((token) => genericTokens.add(token));
  });

  return nameTokens.filter((token) => !genericTokens.has(token));
}

export function isBrandedQuery(query, anchor) {
  const queryLower = (query || '').trim().toLowerCase();
  if (!queryLower) return false;

  const businessName = (anchor?.name || '').trim().toLowerCase();
  if (businessName && queryLower.includes(businessName)) {
    return true;
  }

  const distinctiveTokens = distinctiveNameTokens(anchor);
  if (!distinctiveTokens.length) return false;

  const queryTokens = new Set(tokenizeLabel(queryLower));
  const overlap = distinctiveTokens.filter((token) => queryTokens.has(token)).length;
  if (distinctiveTokens.length === 1) {
    return overlap === 1;
  }
  return overlap >= distinctiveTokens.length;
}

export function sanitizeUserKeyword(userKeyword, anchor) {
  const trimmed = (userKeyword || '').trim();
  if (!trimmed || isBrandedQuery(trimmed, anchor)) {
    return '';
  }
  return trimmed;
}

function isViableMarketResult(result) {
  if (!result || result.isBrandedQuery) return false;
  if (!result.targetFound) return false;
  return (result.competitorCount || 0) >= MIN_VIABLE_COMPETITORS;
}

function uniqueStrings(values) {
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(trimmed);
  });
  return result;
}

function buildQuery(keyword, city, neighborhood) {
  return uniqueStrings([keyword, neighborhood, city]).join(' ');
}

/**
 * Generate candidate Google Maps search queries from an anchor GMB profile.
 */
export function generateKeywordCandidates(anchor, options = {}) {
  const {
    userKeyword = '',
    neighborhood = '',
    manualCity = '',
  } = options;

  const parsed = parseCityFromAddress(anchor?.address || '');
  const city = manualCity.trim() || parsed.city;
  const marketLabel = manualCity.trim()
    ? manualCity.trim()
    : parsed.marketLabel || parsed.city;

  const categoryLabel = resolveCategoryLabel(anchor);

  const mapped = getKeywordsForCategory(categoryLabel);
  const normalizedCategory = normalizeCategoryLabel(categoryLabel);
  const sanitizedUserKeyword = sanitizeUserKeyword(userKeyword, anchor);

  const keywordSeeds = uniqueStrings([
    sanitizedUserKeyword,
    mapped.primary,
    normalizedCategory,
    ...mapped.alternates,
  ]);

  const candidates = [];

  keywordSeeds.forEach((seed) => {
    candidates.push(buildQuery(seed, city, neighborhood));
    if (city) {
      candidates.push(buildQuery(seed, city, ''));
    }
    if (neighborhood && city) {
      candidates.push(buildQuery(seed, neighborhood, city));
    }
  });

  if (city && mapped.primary) {
    candidates.push(`${mapped.primary} near ${city}`);
  }

  return {
    candidates: uniqueStrings(candidates).slice(0, MAX_CANDIDATES),
    city,
    marketLabel,
    categoryLabel,
  };
}

/**
 * Pull neighborhood / sub-city units from a formatted address.
 */
export function extractNeighborhoodsFromAddress(address, city = '') {
  if (!address) return [];

  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  const cityLower = (city || '').trim().toLowerCase();
  const neighborhoods = [];

  for (let index = 1; index < parts.length - 2; index += 1) {
    const part = parts[index];
    if (!part || /^\d/.test(part)) continue;
    if (part.toLowerCase() === cityLower) continue;
    if (/^[A-Z]{2}\s?\d{5}/.test(part) || /^\d{5}/.test(part)) continue;
    neighborhoods.push(part);
  }

  return uniqueStrings(neighborhoods);
}

/**
 * Build keyword queries in priority order: city-wide first, then tighter
 * neighborhood markets until the prospect appears in the top 20.
 */
export function buildPrioritizedKeywordCandidates(anchor, options = {}) {
  const sanitizedUserKeyword = sanitizeUserKeyword(options.userKeyword, anchor);
  const generated = generateKeywordCandidates(anchor, {
    ...options,
    userKeyword: sanitizedUserKeyword,
  });
  const { city, marketLabel } = generated;
  const categoryLabelResolved = resolveCategoryLabel(anchor);

  const mapped = getKeywordsForCategory(categoryLabelResolved);
  const seeds = uniqueStrings([
    sanitizedUserKeyword,
    mapped.primary,
    normalizeCategoryLabel(categoryLabelResolved),
    ...mapped.alternates.slice(0, 5),
  ]).filter(Boolean);

  const neighborhoods = uniqueStrings([
    options.neighborhood,
    ...extractNeighborhoodsFromAddress(anchor?.address, city),
  ]).filter(Boolean);

  const prioritized = [];
  const primary = mapped.primary || normalizeCategoryLabel(categoryLabelResolved);

  if (primary && city) {
    prioritized.push({ query: buildQuery(primary, city, ''), tier: 'city-primary' });
  }

  seeds.forEach((seed) => {
    if (city) {
      prioritized.push({ query: buildQuery(seed, city, ''), tier: 'city' });
    }
  });

  mapped.alternates.forEach((alternate) => {
    if (city) {
      prioritized.push({ query: buildQuery(alternate, city, ''), tier: 'city-alt' });
    }
  });

  if (city && primary) {
    prioritized.push({ query: `${primary} near ${city}`, tier: 'city-near' });
  }

  neighborhoods.forEach((neighborhood) => {
    const hoodSeeds = uniqueStrings([primary, ...seeds.slice(0, 4)]).filter(Boolean);
    hoodSeeds.forEach((seed) => {
      prioritized.push({
        query: buildQuery(seed, city, neighborhood),
        tier: 'neighborhood',
      });
      prioritized.push({
        query: buildQuery(seed, neighborhood, ''),
        tier: 'neighborhood-local',
      });
      prioritized.push({
        query: `${seed} near ${neighborhood}`,
        tier: 'neighborhood-near',
      });
      if (city) {
        prioritized.push({
          query: `${seed} ${neighborhood} ${city}`,
          tier: 'neighborhood-city',
        });
      }
    });
  });

  if (sanitizedUserKeyword && city) {
    prioritized.push({
      query: buildQuery(sanitizedUserKeyword, city, ''),
      tier: 'user-city',
    });
  }

  seeds.forEach((seed) => {
    prioritized.push({ query: seed, tier: 'seed' });
  });

  const seen = new Set();
  const candidates = [];
  prioritized.forEach((item) => {
    const key = item.query.toLowerCase();
    if (!item.query || seen.has(key)) return;
    seen.add(key);
    candidates.push(item);
  });

  return {
    candidates,
    city,
    marketLabel,
    categoryLabel: categoryLabelResolved,
    neighborhoods,
  };
}

function haversineMiles(lat1, lon1, lat2, lon2) {
  if ([lat1, lon1, lat2, lon2].some((value) => value == null)) {
    return null;
  }

  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
}

function normalizePlaceId(placeId) {
  if (!placeId) return '';
  return placeId.replace(/^places\//, '');
}

function isSameBusiness(anchor, competitor) {
  const anchorId = normalizePlaceId(anchor?.place_id);
  const competitorId = normalizePlaceId(competitor?.place_id);
  if (anchorId && competitorId && anchorId === competitorId) {
    return true;
  }

  const anchorName = (anchor?.name || '').trim().toLowerCase();
  const competitorName = (competitor?.name || '').trim().toLowerCase();
  return Boolean(anchorName && competitorName && anchorName === competitorName);
}

function sharesCategory(anchor, competitor) {
  const anchorPrimary = normalizeCategoryLabel(
    anchor?.primary_type_display || anchor?.primary_type || anchor?.primary_category || '',
  );
  const competitorPrimary = normalizeCategoryLabel(
    competitor?.primary_type_display || competitor?.primary_type || '',
  );

  if (anchorPrimary && competitorPrimary) {
    if (anchorPrimary === competitorPrimary) return true;
    if (
      anchorPrimary.includes(competitorPrimary) ||
      competitorPrimary.includes(anchorPrimary)
    ) {
      return true;
    }
  }

  const anchorTypes = new Set((anchor?.types || []).filter((type) => !DIRECTORY_TYPES.has(type)));
  const competitorTypes = (competitor?.types || []).filter((type) => !DIRECTORY_TYPES.has(type));
  return competitorTypes.some((type) => anchorTypes.has(type));
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function hasBuyerIntent(query) {
  const normalized = (query || '').toLowerCase();
  const weakTerms = ['near me', 'best', 'top rated', 'local', 'companies'];
  return !weakTerms.some((term) => normalized.startsWith(term));
}

/**
 * Score a keyword validation result against the selected anchor business.
 */
export function scoreKeywordValidation(anchor, competitors, query) {
  const competitorList = Array.isArray(competitors) ? competitors : [];
  const competitorCount = competitorList.length;

  let targetFound = false;
  let targetPosition = null;

  competitorList.forEach((competitor, index) => {
    if (isSameBusiness(anchor, competitor)) {
      targetFound = true;
      targetPosition = index + 1;
    }
  });

  const categoryMatches = competitorList.filter((competitor) =>
    sharesCategory(anchor, competitor),
  ).length;
  const categoryMatchPct =
    competitorCount > 0 ? Math.round((categoryMatches / competitorCount) * 100) : 0;

  const distances = competitorList
    .map((competitor) =>
      haversineMiles(
        anchor?.latitude,
        anchor?.longitude,
        competitor?.latitude,
        competitor?.longitude,
      ),
    )
    .filter((distance) => distance != null);

  const medianDistanceMiles = median(distances);

  let score = 0;
  if (targetFound) score += 30;
  if (categoryMatchPct >= 75) score += 20;
  else if (categoryMatchPct >= 50) score += 10;
  if (competitorCount >= 15) score += 15;
  else if (competitorCount >= 10) score += 10;
  else if (competitorCount >= 8) score += 5;

  if (medianDistanceMiles != null) {
    if (medianDistanceMiles <= 10) score += 15;
    else if (medianDistanceMiles <= 20) score += 10;
    else if (medianDistanceMiles <= 35) score += 5;
  }

  if (hasBuyerIntent(query)) score += 10;

  const mapped = getKeywordsForCategory(resolveCategoryLabel(anchor));
  const queryLower = (query || '').toLowerCase();
  if (
    mapped.primary &&
    (queryLower.includes(mapped.primary) ||
      mapped.alternates.some((alt) => queryLower.includes(alt.toLowerCase())))
  ) {
    score += 10;
  }

  if (categoryMatchPct < 40 && competitorCount >= 8) score -= 20;
  if (!targetFound && competitorCount >= 15 && categoryMatchPct < 50) score -= 20;
  if (competitorCount < 8) score -= 30;
  if (competitorCount < MIN_VIABLE_COMPETITORS) score -= 25;

  const brandedQuery = isBrandedQuery(query, anchor);
  if (brandedQuery) score -= 50;

  score = Math.max(0, Math.min(100, score));

  return {
    query,
    score,
    targetFound,
    targetPosition,
    categoryMatchPct,
    competitorCount,
    medianDistanceMiles,
    qualityLabel: getQualityLabel(score),
    isBrandedQuery: brandedQuery,
  };
}

export function getQualityLabel(score) {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 55) return 'Medium';
  return 'Weak';
}

/**
 * Pick the best keyword from scored validation results.
 * Only considers keywords where the prospect appears in the top 20 when
 * any such result exists.
 */
export function pickBestKeywordResult(results) {
  if (!results?.length) return null;

  const viable = results.filter(
    (result) => isViableMarketResult(result) &&
      (result.competitorCount || 0) >= MIN_VIABLE_COMPETITORS,
  );

  let pool = viable;
  if (!pool.length) {
    const withTarget = results.filter(
      (result) =>
        result.targetFound &&
        !result.isBrandedQuery &&
        (result.competitorCount || 0) >= 8,
    );
    if (withTarget.length) {
      pool = withTarget;
    } else {
      const broad = results.filter(
        (result) =>
          !result.isBrandedQuery &&
          (result.competitorCount || 0) >= MIN_VIABLE_COMPETITORS,
      );
      pool = broad.length ? broad : results;
    }
  }

  return [...pool].sort((a, b) => {
    if (a.isBrandedQuery !== b.isBrandedQuery) {
      return Number(a.isBrandedQuery) - Number(b.isBrandedQuery);
    }
    if (a.targetFound && b.targetFound) {
      const posA = a.targetPosition || 99;
      const posB = b.targetPosition || 99;
      if (posA !== posB) return posA - posB;
    }
    if (Boolean(a.targetFound) !== Boolean(b.targetFound)) {
      return Number(b.targetFound) - Number(a.targetFound);
    }
    if (b.score !== a.score) return b.score - a.score;
    return (b.competitorCount || 0) - (a.competitorCount || 0);
  })[0];
}

/**
 * Resolve the best market keyword by validating candidate queries.
 *
 * @param {object} anchor - Selected GMB business
 * @param {object} options - Resolver options
 * @param {function} options.validateQuery - async (query) => competitors[]
 */
export async function resolveMarketKeyword(anchor, options = {}) {
  const {
    userKeyword = '',
    neighborhood = '',
    manualCity = '',
    validateQuery,
    maxValidations = MAX_VALIDATIONS_DEFAULT,
  } = options;

  if (!anchor || typeof validateQuery !== 'function') {
    throw new Error('Anchor business and validateQuery are required');
  }

  const built = buildPrioritizedKeywordCandidates(anchor, {
    userKeyword,
    neighborhood,
    manualCity,
  });

  const validations = [];

  for (const { query, tier } of built.candidates.slice(0, maxValidations)) {
    try {
      const competitors = await validateQuery(query);
      const result = scoreKeywordValidation(anchor, competitors, query);
      validations.push({ ...result, tier });

      if (isViableMarketResult(result)) {
        break;
      }
    } catch (_error) {
      validations.push({
        ...scoreKeywordValidation(anchor, [], query),
        tier,
      });
    }
  }

  const recommended = pickBestKeywordResult(validations);
  const targetResolved = isViableMarketResult(recommended);

  return {
    recommended,
    alternates: [...validations].sort((a, b) => b.score - a.score),
    targetResolved,
    city: built.city,
    marketLabel: built.marketLabel,
    categoryLabel: built.categoryLabel,
    neighborhoodsTried: built.neighborhoods,
  };
}
